import { Check, Plus, ChevronRight } from "lucide-react";
import { ProgressBar, PRIORITY_META } from "../ui-bits";
import { useData } from "../../contexts/DataContext";
import { cn } from "../../lib/utils";

function flattenSubs(subs, depth, out = []) {
  for (const s of subs) {
    out.push({ s, depth });
    flattenSubs(s.children || [], depth + 1, out);
  }
  return out;
}

function SubtaskRow({ sub, depth, toggleSubtask, onOpen }) {
  return (
    <div data-testid={`panel-subtask-${sub.id}`} className="flex items-center gap-2 py-1 group panel-indent" style={{ "--d": depth }}>
      <button data-testid={`panel-subtask-toggle-${sub.id}`} onClick={() => toggleSubtask(sub)}
        className={cn("h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
          sub.status === "done" ? "bg-[#10B981] border-[#10B981] text-white" : "border-muted-foreground/50 hover:border-[#10B981]")}>
        {sub.status === "done" && <Check size={10} strokeWidth={3} />}
      </button>
      <button data-testid={`panel-subtask-open-${sub.id}`} onClick={() => onOpen(sub.id)}
        className={cn("text-sm flex-1 truncate text-left hover:text-[#E31E24] transition-colors", sub.status === "done" && "line-through text-muted-foreground")}>
        {sub.title}
      </button>
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_META[sub.priority]?.color }} />
      <ChevronRight size={12} className="text-muted-foreground/40 shrink-0" />
    </div>
  );
}

export default function SubtasksSection({ task, subtasks, toggleSubtask, subtaskTitle, setSubtaskTitle, addSubtask }) {
  const { setOpenTaskId } = useData();
  const doneSubs = subtasks.filter((s) => s.status === "done").length;
  const pct = subtasks.length ? Math.round((doneSubs / subtasks.length) * 100) : task.status === "done" ? 100 : 0;
  const canAdd = (task.depth || 1) < 6;
  const rows = flattenSubs(subtasks, 0);

  return (
    <div className="p-4 border-b border-border" data-testid="panel-subtasks">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Subtasks · {doneSubs}/{subtasks.length} <span className="normal-case tracking-normal">(nested, max 5 levels)</span></p>
        <span className="text-xs font-semibold text-muted-foreground">{pct}%</span>
      </div>
      <ProgressBar value={pct} className="mb-3" />
      <div className="space-y-1">
        {rows.map(({ s, depth }) => (
          <SubtaskRow key={s.id} sub={s} depth={depth} toggleSubtask={toggleSubtask} onOpen={setOpenTaskId} />
        ))}
      </div>
      {canAdd ? (
        <div className="flex items-center gap-2 mt-2">
          <Plus size={14} className="text-muted-foreground" />
          <input data-testid="panel-add-subtask-input" value={subtaskTitle} onChange={(e) => setSubtaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSubtask()} placeholder="Add subtask, press Enter"
            className="flex-1 h-7 px-2 text-sm rounded border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="panel-subtasks-max-depth">Maximum nesting depth (5 levels) reached.</p>
      )}
    </div>
  );
}
